// Board view — three-sector layout. The dispatcher sees every gate at
// once, no scrolling. Sectors mirror the canonical /flight-data app:
//   Sector 1 → C23–C39 (17 gates)
//   Sector 2 → C40–C55 (16 gates)
//   Sector 3 → C56–C67 (12 gates)
//
// Each sector renders its gates as a 2-column grid of compact cards.
// Clicking a card expands it to fill its sector while the other two
// sectors stay compact and visible. Click X (or the same card again)
// to collapse back.
//
// Color philosophy (matches GateCard):
//   Quiet gates = neutral chrome (recede into the background).
//   Color = urgency (alertLevelFor → red / gold / blue).

import React, { useMemo } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import StickyNote2Icon from '@mui/icons-material/StickyNote2';
import {
  GATE_DETAILS, alertLevelFor, ALERT_COLORS, nextInboundFor,
} from './mockGateDetails';
import { useIntel } from './IntelligenceContext';
import { useLiveGateDetail } from './useLiveDetail';
import { C } from '../styles';

const QUIET_TEXT   = 'rgba(255, 255, 255, 0.55)';
const QUIET_BORDER = 'rgba(255, 255, 255, 0.10)';

// Sector definitions — exact match for the canonical DispatchBoard.
const SECTORS = [
  { id: 'sector1', label: 'Sector 1', from: 23, to: 39 },
  { id: 'sector2', label: 'Sector 2', from: 40, to: 55 },
  { id: 'sector3', label: 'Sector 3', from: 56, to: 67 },
];

// Phase label only — no color (color comes from alert level).
const PHASE_LABEL = {
  empty: 'EMPTY', inbound: 'INBOUND', arriving: 'AT GATE',
  download: 'DOWNLOAD', upload: 'UPLOAD', push: 'PUSH',
};

// Synthesize a minimal "empty" detail for gates without a current plane,
// so the compact grid can still show every gate uniformly.
function emptyDetailFor(gateId, gateNum) {
  return {
    gateId, gateNum,
    flightNumber: null, origin: null, eta: null, etd: null,
    state: 'empty', phase: 'empty',
    drivers: [], connections: [], issues: [],
    bins: [], totalBags: 0, scannedBags: 0, bagPct: 0,
    tug: null, phaseStartedSecAgo: 0,
    nextInbound: nextInboundFor(gateId),
  };
}

function detailForGate(gateId, gateNum) {
  return GATE_DETAILS.get(gateId) || emptyDetailFor(gateId, gateNum);
}

function BoardView() {
  const intel = useIntel();
  const { hasAnnotations, selected, setSelected, clearSelection } = intel;

  // Expansion state lives in IntelligenceContext (`selected`) so the
  // GateDetailCard's built-in X — which calls clearSelection() — closes
  // the expanded view. Local state would diverge.
  const expandedGateId = (selected?.kind === 'gate') ? selected.id : null;
  const setExpandedGateId = (id) => {
    if (id == null) clearSelection();
    else setSelected({ kind: 'gate', id });
  };

  // Build per-sector gate lists (with details cached).
  const sectorRows = useMemo(() => SECTORS.map(s => {
    const gates = [];
    for (let n = s.from; n <= s.to; n++) {
      const id = `C${n}`;
      gates.push({ id, num: n, detail: detailForGate(id, n) });
    }
    return { ...s, gates };
  }), []);

  // Whole-board counts for the toolbar — drives "X red / Y gold / Z blue".
  const totals = useMemo(() => {
    let red = 0, gold = 0, blue = 0;
    for (const sector of sectorRows) {
      for (const g of sector.gates) {
        const lvl = alertLevelFor(g.detail);
        if (lvl === 'red')  red++;
        else if (lvl === 'gold') gold++;
        else if (lvl === 'blue') blue++;
      }
    }
    return { red, gold, blue };
  }, [sectorRows]);

  return (
    <Box sx={{
      flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column',
      backgroundColor: '#0a0d14',
    }}>
      {/* Toolbar — counts only, no filters (everything is visible) */}
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1.25,
        px: 2.5, py: 1.5,
        borderBottom: `1px solid ${C.border}`,
        backgroundColor: C.surface,
        backdropFilter: C.blur,
        WebkitBackdropFilter: C.blur,
        flexShrink: 0,
      }}>
        <Typography sx={{
          fontSize: '0.75rem', color: C.textMuted, fontWeight: 700,
          letterSpacing: '0.1em', textTransform: 'uppercase',
        }}>
          Live Board
        </Typography>
        <Box sx={{ flex: 1 }} />
        <CountChip color="#FF0000"  label="Act now" value={totals.red} />
        <CountChip color="#F9B612"  label="Heads-up" value={totals.gold} />
        <CountChip color="#0277BD"  label="Info"     value={totals.blue} />
      </Box>

      {/* Sectors — three columns, each filling its third of the screen */}
      <Box sx={{
        flex: 1, minHeight: 0,
        display: 'flex', gap: 1.25, p: 1.25,
      }}>
        {sectorRows.map(sector => {
          const expandedGate = sector.gates.find(g => g.id === expandedGateId);
          return (
            <SectorPanel
              key={sector.id}
              sector={sector}
              expandedGate={expandedGate || null}
              onExpand={(id) => setExpandedGateId(id)}
              onCollapse={() => setExpandedGateId(null)}
              hasAnnotations={hasAnnotations}
            />
          );
        })}
      </Box>
    </Box>
  );
}

// ── Sector panel ─────────────────────────────────────────────────────

function SectorPanel({ sector, expandedGate, onExpand, onCollapse, hasAnnotations }) {
  const sectorAlertCount = sector.gates.filter(g => alertLevelFor(g.detail)).length;

  return (
    <Box sx={{
      flex: 1, minWidth: 0,
      display: 'flex', flexDirection: 'column',
      backgroundColor: 'rgba(17, 25, 36, 0.35)',
      backdropFilter: 'blur(8px)',
      WebkitBackdropFilter: 'blur(8px)',
      border: `1px solid ${C.border}`,
      borderRadius: 1.5,
      overflow: 'hidden',
    }}>
      {/* Sector header */}
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1,
        px: 1.5, py: 1,
        borderBottom: `1px solid ${C.border}`,
        backgroundColor: 'rgba(0, 0, 0, 0.25)',
        flexShrink: 0,
      }}>
        <Typography sx={{
          fontSize: '0.85rem', color: C.textPrimary, fontWeight: 700,
          letterSpacing: '0.08em', textTransform: 'uppercase',
        }}>
          {sector.label}
        </Typography>
        <Typography sx={{ fontSize: '0.7rem', color: C.textMuted, fontWeight: 600 }}>
          C{sector.from}–C{sector.to} · {sector.gates.length} gates
        </Typography>
        {sectorAlertCount > 0 && (
          <Typography sx={{
            ml: 'auto', fontSize: '0.7rem', color: C.accent,
            fontWeight: 700, letterSpacing: '0.06em',
          }}>
            {sectorAlertCount} flagged
          </Typography>
        )}
      </Box>

      {/* Body — compact 2-col grid. The selected gate's full detail is
          rendered as a draggable popout card by the Workspace, NOT
          inline here, so the dispatcher only ever sees one card. */}
      <Box sx={{
        flex: 1, minHeight: 0, position: 'relative',
        overflow: 'hidden',
      }}>
        <Box sx={{
          position: 'absolute', inset: 0,
          p: 1, overflow: 'auto',
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gridAutoRows: 'min-content',
          gap: 0.75,
          '&::-webkit-scrollbar': { width: 6 },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(255, 255, 255, 0.15)', borderRadius: 3,
          },
        }}>
          {sector.gates.map(g => (
            <CompactCard
              key={g.id}
              gate={g}
              onClick={() => onExpand(g.id)}
              hasNote={hasAnnotations({ kind: 'gate', id: g.id })}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
}

// ── Compact card (one per gate in the 2-col grid) ─────────────────────

const COMPACT_H = 64;

function CompactCard({ gate, onClick, hasNote }) {
  // Live tick so bag % / countdowns stay current.
  const detail = useLiveGateDetail(gate.detail);
  const alertLevel = alertLevelFor(detail);
  const alertColor = alertLevel ? ALERT_COLORS[alertLevel] : null;
  const isEmpty = detail.phase === 'empty';
  const flightNum = detail.flightNumber ? detail.flightNumber.replace(/^WN\s*/, '') : null;
  const hottest = detail.connections.filter(c => c.isHot).sort((a, b) => a.connectionMin - b.connectionMin)[0];
  const flameCritical = hottest && hottest.connectionMin <= 15;

  return (
    <Paper
      elevation={0}
      onClick={onClick}
      sx={{
        height: COMPACT_H,
        display: 'flex', flexDirection: 'column',
        cursor: 'pointer',
        position: 'relative',
        backgroundColor: alertColor ? `${alertColor}10` : 'rgba(20, 20, 25, 0.55)',
        border: `1px solid ${alertColor ? `${alertColor}77` : QUIET_BORDER}`,
        borderRadius: 1,
        px: 0.9, py: 0.6,
        transition: 'transform 140ms ease, background-color 140ms, box-shadow 140ms',
        '&:hover': {
          transform: 'translateY(-1px)',
          backgroundColor: alertColor ? `${alertColor}1A` : 'rgba(255, 255, 255, 0.05)',
          boxShadow: '0 4px 10px rgba(0, 0, 0, 0.40)',
        },
        // Pulse only on red — true "act now" signal.
        ...(alertLevel === 'red' && {
          animation: 'compactPulse 1.6s ease-in-out infinite',
          '@keyframes compactPulse': {
            '0%, 100%': { boxShadow: `0 0 0 0 ${alertColor}40` },
            '50%':      { boxShadow: `0 0 0 3px ${alertColor}30, 0 0 12px ${alertColor}55` },
          },
        }),
      }}
    >
      {/* Top row — gate # · flight # · hot flame */}
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
        <Typography sx={{
          fontSize: '0.85rem', color: C.textPrimary, fontWeight: 800,
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          letterSpacing: 0.4,
        }}>
          {gate.id}
        </Typography>
        {flightNum && (
          <Typography sx={{
            fontSize: '0.78rem', color: C.textPrimary, fontWeight: 700,
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          }}>
            {flightNum}
          </Typography>
        )}
        <Box sx={{ flex: 1 }} />
        {hottest && (
          <WhatshotIcon sx={{
            fontSize: 13,
            color: flameCritical ? '#FF0000' : QUIET_TEXT,
          }} />
        )}
        {hasNote && (
          <StickyNote2Icon sx={{ fontSize: 11, color: C.accent }} />
        )}
        {alertLevel && (
          <Box sx={{
            width: 7, height: 7, borderRadius: '50%',
            backgroundColor: alertColor,
            boxShadow: `0 0 5px ${alertColor}AA`,
          }} />
        )}
      </Box>

      {/* Bottom row — phase + secondary info (next ETA for empties, etc.) */}
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.6, mt: 'auto' }}>
        <Typography sx={{
          fontSize: '0.62rem', color: QUIET_TEXT, fontWeight: 700,
          letterSpacing: 0.6, textTransform: 'uppercase',
        }}>
          {PHASE_LABEL[detail.phase] || detail.phase}
        </Typography>
        <Box sx={{ flex: 1 }} />
        {isEmpty && detail.nextInbound ? (
          <Typography sx={{
            fontSize: '0.68rem', color: QUIET_TEXT,
            fontVariantNumeric: 'tabular-nums',
          }}>
            next {detail.nextInbound.eta}
          </Typography>
        ) : !isEmpty ? (
          <Typography sx={{
            fontSize: '0.68rem', color: QUIET_TEXT,
            fontVariantNumeric: 'tabular-nums',
          }}>
            {detail.eta} → {detail.etd}
          </Typography>
        ) : null}
      </Box>
    </Paper>
  );
}

// ── Toolbar count chip ───────────────────────────────────────────────

function CountChip({ color, label, value }) {
  const muted = value === 0;
  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 0.5,
      px: 1, py: 0.4,
      backgroundColor: muted ? 'transparent' : `${color}14`,
      border: `1px solid ${muted ? C.border : `${color}55`}`,
      borderRadius: 1,
      opacity: muted ? 0.55 : 1,
    }}>
      <Box sx={{
        width: 8, height: 8, borderRadius: '50%',
        backgroundColor: color, opacity: muted ? 0.4 : 1,
      }} />
      <Typography sx={{
        fontSize: '0.72rem', color: muted ? C.textMuted : color,
        fontWeight: 700, letterSpacing: '0.05em',
      }}>
        {value} {label}
      </Typography>
    </Box>
  );
}

export default BoardView;
